import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    MenuItem, Typography, ToggleButton, ToggleButtonGroup, CircularProgress, Alert
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { EMAR_STATUS, clientClock, readApiError } from '../../../utils/emarStatus';

const RECORDED = ['onTime', 'late', 'notGiven'];
const API_FMT = 'YYYY-MM-DD HH:mm:ss';

/**
 * Charts one scheduled dose as given or not given (FWAPI POST /emar/given | /emar/not-given).
 *
 * The same dialog corrects a dose that was already charted: the record is one row per
 * dose, so saving again overwrites it, and for a client synced to AbilityHub the
 * change lands in the shared medication audit trail with the reason given here.
 */
function DoseRecordDialog({ dose, codes, empId, onClose, onSaved }) {
    const alreadyRecorded = !!dose && RECORDED.includes(dose.status);

    const [mode, setMode] = useState('given');
    const [takenAt, setTakenAt] = useState(null);
    const [codeID, setCodeID] = useState('');
    const [notes, setNotes] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Prefill from whatever is on record, so a correction starts from the entry.
    useEffect(() => {
        if (!dose) return;

        const isNotGiven = dose.status === 'notGiven';
        const recordedCode = isNotGiven ? dose.reasonCode : dose.outcomeCode;
        const list = (isNotGiven ? codes.reason : codes.outcome) || [];

        setMode(isNotGiven ? 'notGiven' : 'given');
        setTakenAt(dose.takenAt ? moment(dose.takenAt, API_FMT) : moment().seconds(0));
        setCodeID(list.find(c => c.code === recordedCode)?.codeID || '');
        setNotes(dose.notes || '');
        setReason('');
        setError(null);
    }, [dose, codes]);

    const options = (mode === 'given' ? codes.outcome : codes.reason) || [];
    const selectedCode = options.find(c => c.codeID === codeID);
    const noteRequired = !!selectedCode?.requiresNote;

    const validation = useMemo(() => {
        if (mode === 'notGiven' && !codeID) return 'Select a reason.';
        if (noteRequired && !notes.trim()) return 'A note is required for this selection.';
        if (mode === 'given') {
            if (!takenAt || !takenAt.isValid()) return 'Enter the time the dose was given.';
            if (takenAt.isAfter(moment().add(1, 'minute'))) return 'The time given cannot be in the future.';
        }
        return null;
    }, [mode, codeID, noteRequired, notes, takenAt]);

    const switchMode = (_, next) => {
        if (!next || next === mode) return;
        setMode(next);
        setCodeID('');
    };

    const handleSave = async () => {
        if (validation) return;

        setSaving(true);
        setError(null);
        try {
            const { tzOffset } = clientClock();
            const body = {
                customer_medication_id: dose.customer_medication_id,
                scheduledFor: dose.scheduledFor,
                tzOffset,
                intakeCodeID: codeID || null,
                notes: notes.trim() || null,
                reason: reason.trim() || null,
                empId,
            };

            if (mode === 'given') {
                body.takenAt = moment(takenAt).seconds(0).format(API_FMT);
                await axios.post(APIURL + '/emar/given', body);
            } else {
                await axios.post(APIURL + '/emar/not-given', body);
            }

            onSaved();
        } catch (err) {
            console.log(err);
            setError(readApiError(err, 'Could not save the dose. Please try again.'));
        }
        setSaving(false);
    };

    if (!dose) return null;

    return (
        <Dialog open={!!dose} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle style={{ paddingBottom: 4 }}>
                {alreadyRecorded ? 'Edit dose record' : 'Record dose'}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" fontWeight={600}>
                    {dose.name}{dose.dosage ? ` — ${dose.dosage}` : ''}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    Scheduled {moment(dose.scheduledFor, API_FMT).format('ddd, MMM D · h:mm A')}
                    {alreadyRecorded && ` · currently ${EMAR_STATUS[dose.status].short.toLowerCase()}`}
                </Typography>

                <ToggleButtonGroup value={mode} exclusive onChange={switchMode} size="small" fullWidth>
                    <ToggleButton value="given" style={{ textTransform: 'none' }}>Given</ToggleButton>
                    <ToggleButton value="notGiven" style={{ textTransform: 'none' }}>Not given</ToggleButton>
                </ToggleButtonGroup>

                {mode === 'given' && (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            label="Time given"
                            value={takenAt}
                            onChange={setTakenAt}
                            maxDateTime={moment()}
                            format="MM/DD/YYYY hh:mm A"
                            slotProps={{ textField: { size: 'small', fullWidth: true, style: { marginTop: 14 } } }}
                        />
                    </LocalizationProvider>
                )}

                <TextField
                    select
                    label={mode === 'given' ? 'Outcome (optional)' : 'Reason not given'}
                    required={mode === 'notGiven'}
                    size="small"
                    fullWidth
                    value={codeID}
                    onChange={e => setCodeID(e.target.value)}
                    style={{ marginTop: 14 }}
                >
                    {mode === 'given' && <MenuItem value=""><em>None</em></MenuItem>}
                    {options.map(c => (
                        <MenuItem key={c.codeID} value={c.codeID}>{c.label}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label={noteRequired ? 'Note (required)' : 'Note (optional)'}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    inputProps={{ maxLength: 512 }}
                    style={{ marginTop: 14 }}
                />

                {alreadyRecorded && (
                    <TextField
                        label="Reason for change (optional)"
                        size="small"
                        fullWidth
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        inputProps={{ maxLength: 512 }}
                        style={{ marginTop: 14 }}
                    />
                )}

                {error && <Alert severity="error" style={{ marginTop: 14 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || !!validation} title={validation || ''}>
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DoseRecordDialog;
